/**
 * Inclusive list of `YYYY-MM` labels between two `YYYY-MM-DD` dates.
 *
 * The monthly aggregate endpoint omits months with no qualifying orders, so a
 * chart that plots the response positionally would place November beside
 * February as though they were adjacent. Charts build their category axis from
 * this instead and map buckets onto it, leaving real gaps visible.
 */
/**
 * The inclusive `YYYY-MM-DD` bounds of a single `YYYY-MM` bucket.
 *
 * The inverse of {@link monthRange}: that turns a date range into month labels
 * for the chart axis, this turns one of those labels back into the date range
 * the filters speak in, so clicking a month bar can drill through to it.
 *
 * Returns null for anything that isn't a well-formed month, so a malformed
 * bucket label becomes "no navigation" rather than a filter built from NaN.
 */
export function monthBounds(
  month: string
): { start: string; end: string } | null {
  if (!/^\d{4}-\d{2}$/.test(month)) return null;

  const year = Number(month.slice(0, 4));
  const monthNo = Number(month.slice(5, 7));
  if (monthNo < 1 || monthNo > 12) return null;

  // Day 0 of the *next* month is the last day of this one, which gets February
  // and leap years right without a table of month lengths.
  const lastDay = new Date(Date.UTC(year, monthNo, 0)).getUTCDate();

  return {
    start: `${month}-01`,
    end: `${month}-${String(lastDay).padStart(2, '0')}`,
  };
}

export function monthRange(startDate: string, endDate: string): string[] {
  if (!startDate || !endDate) return [];

  const start = new Date(`${startDate.slice(0, 7)}-01T00:00:00.000Z`);
  const end = new Date(`${endDate.slice(0, 7)}-01T00:00:00.000Z`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  if (start > end) return [];

  const months: string[] = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    const year = cursor.getUTCFullYear();
    const month = String(cursor.getUTCMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }

  return months;
}

/**
 * Year-to-date bounds as the `YYYY-MM-DD` strings the filters speak in:
 * 1 January of `today`'s year through `today` itself, inclusive.
 *
 * Built from the LOCAL date parts rather than toISOString, which would shift to
 * UTC and hand back yesterday for anyone east of Greenwich in the evening — the
 * date picker shows local dates, so the default has to agree with it.
 *
 * `today` is a parameter so the behaviour is testable without mocking the clock.
 */
export function ytdRange(today: Date = new Date()): {
  start: string;
  end: string;
} {
  const pad = (n: number): string => String(n).padStart(2, '0');
  const year = today.getFullYear();

  return {
    start: `${year}-01-01`,
    end: `${year}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
  };
}
